import CryptoJS from 'crypto-js'

const STORAGE_KEY = 'keyStored'

const APP_SECRET = import.meta.env.VITE_KEYSTORE_SECRET || 'CHANGE_ME'

function deviceFingerprint() {
  return [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join('|')
}

type RememberedKeyPayload = {
  createdAt: number
  keyFileB64: string
  password: string
  meta?: {
    fileName?: string
    caCN?: string
  }
}

function uint8ToB64(bytes: Uint8Array): string {
  let bin = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(bin)
}

function b64ToUint8(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function buildKeystorePassphrase(): string {
  return `${APP_SECRET}::${deviceFingerprint()}`
}

function hasRememberedKey(): boolean {
  return (
    typeof localStorage !== 'undefined' && !!localStorage.getItem(STORAGE_KEY)
  )
}

function clearRememberedKey(): void {
  localStorage.removeItem(STORAGE_KEY)
}

function saveRememberedKey(params: {
  keyBytes: Uint8Array
  password: string
  meta: RememberedKeyPayload['meta']
}): void {
  const payload: RememberedKeyPayload = {
    createdAt: Date.now(),
    keyFileB64: uint8ToB64(params.keyBytes),
    password: params.password,
    meta: params.meta,
  }

  const json = JSON.stringify(payload)
  const passphrase = buildKeystorePassphrase()

  const encrypted = CryptoJS.AES.encrypt(json, passphrase).toString()

  localStorage.setItem(STORAGE_KEY, encrypted)
}

function loadRememberedKey(): {
  keyBytes: Uint8Array
  password: string
  meta?: RememberedKeyPayload['meta']
  createdAt: number
} | null {
  const encrypted = localStorage.getItem(STORAGE_KEY)
  if (!encrypted) return null

  const passphrase = buildKeystorePassphrase()

  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, passphrase)
    const json = bytes.toString(CryptoJS.enc.Utf8)
    if (!json) return null

    const payload = JSON.parse(json) as RememberedKeyPayload

    return {
      keyBytes: b64ToUint8(payload.keyFileB64),
      password: payload.password,
      meta: payload.meta,
      createdAt: payload.createdAt,
    }
  } catch {
    return null
  }
}

export {
  hasRememberedKey,
  clearRememberedKey,
  saveRememberedKey,
  loadRememberedKey,
}
